/**
 * ShiftManagement.js
 *
 * FIX: employee_count showing 0 even when employees are assigned.
 *
 * ROOT CAUSE: GET /shifts returns employee_count = 0 because shift assignments
 * go into the shift_assignments/employee_shifts table (is_active=1), not a
 * count column. The only reliable source is GET /shifts/:id/employees —
 * the same endpoint that correctly shows 12 in the modal.
 *
 * FIX: After loading shifts, call GET /shifts/:id/employees in parallel
 * for every shift and use the array length as the count.
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal,
    Form, Spinner, ListGroup,
} from 'react-bootstrap';
import { Clock, Plus, Edit2, Trash2, Users, UserPlus, X } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const blank = {
    name: '', start_time: '09:00', end_time: '18:00',
    break_minutes: 60, days_of_week: [1, 2, 3, 4, 5],
};

// ── Confirm Modal ──────────────────────────────────────────────────────────────
const ConfirmModal = ({ show, title, message, onConfirm, onCancel, loading }) => (
    <Modal show={show} onHide={onCancel} centered size="sm">
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold fs-6">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-2">
            <p className="text-muted small mb-0">{message}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-2">
            <Button variant="light" onClick={onCancel} disabled={loading}>Cancel</Button>
            <Button variant="danger" onClick={onConfirm} disabled={loading}>
                {loading ? <Spinner size="sm"/> : 'Delete'}
            </Button>
        </Modal.Footer>
    </Modal>
);

// ── Validation ─────────────────────────────────────────────────────────────────
const toMins = (t) => {
    if (!t) return null;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
};
const validate = (form) => {
    const e = {};
    const name = form.name?.trim();
    if (!name)                 e.name = 'Shift name is required';
    else if (name.length < 2)  e.name = 'Name must be at least 2 characters';
    else if (name.length > 80) e.name = 'Name cannot exceed 80 characters';
    if (!form.start_time)      e.start_time = 'Start time is required';
    if (!form.end_time)        e.end_time   = 'End time is required';
    if (form.start_time && form.end_time) {
        const startM = toMins(form.start_time);
        const endM   = toMins(form.end_time);
        if (startM === endM) {
            e.end_time = 'End time cannot be the same as start time';
        } else {
            const netMins = endM > startM
                ? endM - startM
                : (24 * 60 - startM) + endM;
            const brk = parseInt(form.break_minutes) || 0;
            if (brk < 0)         e.break_minutes = 'Break cannot be negative';
            else if (brk > 480)  e.break_minutes = 'Break cannot exceed 8 hours (480 min)';
            else if (brk >= netMins)
                e.break_minutes = `Break (${brk}m) must be less than total shift duration (${netMins}m)`;
        }
    }
    if (!form.days_of_week || form.days_of_week.length === 0)
        e.days_of_week = 'Select at least one working day';
    return e;
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const workHours = (start, end, brk) => {
    if (!start || !end) return '—';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm) - (parseInt(brk) || 0);
    if (mins < 0) mins += 24 * 60;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

// ── Main Component ─────────────────────────────────────────────────────────────
const ShiftManagement = () => {
    const [shifts,    setShifts]    = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [modal,     setModal]     = useState({ show: false, editing: null });
    const [empModal,  setEmpModal]  = useState({ show: false, shift: null, shiftEmployees: [] });
    const [form,      setForm]      = useState(blank);
    const [errors,    setErrors]    = useState({});
    const [attempted, setAttempted] = useState(false);
    const [saving,    setSaving]    = useState(false);
    const [assignEmp, setAssignEmp] = useState('');
    const [confirm,   setConfirm]   = useState({ show: false, id: null, name: '', loading: false });

    const fetchShifts = async () => {
        setLoading(true);
        try {
            // Step 1: Load shifts + employee list in parallel
            const [shiftRes, empRes] = await Promise.all([
                axiosInstance.get('/shifts'),
                axiosInstance.get('/employees', { params: { limit: 200 } }),
            ]);

            const rawShifts    = shiftRes.data.data || [];
            const rawEmployees = empRes.data.data?.employees || empRes.data.data || [];

            // Step 2: For each shift, fetch the actual assigned employee list.
            // GET /shifts/:id/employees is the authoritative source — same endpoint
            // the modal uses and which correctly returns 12 employees.
            // We fire all requests in parallel so it's fast (one round-trip).
            const countPromises = rawShifts.map(s =>
                axiosInstance.get(`/shifts/${s.id}/employees`)
                    .then(r => ({
                        id:    s.id,
                        count: Array.isArray(r.data.data) ? r.data.data.length : 0,
                    }))
                    .catch(() => ({ id: s.id, count: 0 }))
            );

            const countResults = await Promise.all(countPromises);

            // Build id → count lookup
            const countMap = {};
            countResults.forEach(({ id, count }) => { countMap[id] = count; });

            // Step 3: Merge counts into shift objects
            const enriched = rawShifts.map(s => ({
                ...s,
                employee_count: countMap[s.id] ?? 0,
            }));

            setShifts(enriched);
            setEmployees(
                rawEmployees.map(e => ({
                    id:   e.id || e.user_id,
                    name: `${e.first_name} ${e.last_name}`,
                    code: e.employee_code,
                }))
            );
        } catch (err) {
            toast.error('Failed to load shifts');
            console.error('[ShiftManagement] fetchShifts error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchShifts(); }, []);

    const parseDays = (d) =>
        typeof d === 'string' ? JSON.parse(d) : (d || [1, 2, 3, 4, 5]);

    const openCreate = () => {
        setModal({ show: true, editing: null });
        setForm(blank);
        setErrors({});
        setAttempted(false);
    };
    const openEdit = (s) => {
        setModal({ show: true, editing: s });
        setForm({
            name:          s.name,
            start_time:    s.start_time,
            end_time:      s.end_time,
            break_minutes: s.break_minutes || 60,
            days_of_week:  parseDays(s.days_of_week),
        });
        setErrors({});
        setAttempted(false);
    };
    const closeModal = () => {
        setModal({ show: false, editing: null });
        setErrors({});
        setAttempted(false);
    };
    const setField = (field, value) => {
        setForm(p => ({ ...p, [field]: value }));
        if (attempted)
            setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    };
    const toggleDay = (day) => {
        const next = form.days_of_week.includes(day)
            ? form.days_of_week.filter(d => d !== day)
            : [...form.days_of_week, day].sort((a, b) => a - b);
        setField('days_of_week', next);
    };
    const handleSave = async () => {
        setAttempted(true);
        const errs = validate(form);
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setSaving(true);
        try {
            if (modal.editing) {
                await axiosInstance.put(`/shifts/${modal.editing.id}`, form);
                toast.success('Shift updated');
            } else {
                await axiosInstance.post('/shifts', form);
                toast.success('Shift created');
            }
            closeModal();
            fetchShifts();
        } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
        finally { setSaving(false); }
    };
    const handleDeleteConfirm = async () => {
        setConfirm(p => ({ ...p, loading: true }));
        try {
            await axiosInstance.delete(`/shifts/${confirm.id}`);
            toast.success('Shift deleted');
            fetchShifts();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Cannot delete shift with assigned employees');
        } finally {
            setConfirm({ show: false, id: null, name: '', loading: false });
        }
    };
    const openEmpModal = async (shift) => {
        try {
            const res = await axiosInstance.get(`/shifts/${shift.id}/employees`);
            setEmpModal({ show: true, shift, shiftEmployees: res.data.data || [] });
            setAssignEmp('');
        } catch { toast.error('Failed to load shift employees'); }
    };
    const handleAssign = async () => {
        if (!assignEmp) return;
        try {
            await axiosInstance.post(`/shifts/${empModal.shift.id}/assign`, {
                employee_id: parseInt(assignEmp),
            });
            toast.success('Employee assigned');
            const res = await axiosInstance.get(`/shifts/${empModal.shift.id}/employees`);
            setEmpModal(p => ({ ...p, shiftEmployees: res.data.data || [] }));
            setAssignEmp('');
            fetchShifts();
        } catch (err) { toast.error(err.response?.data?.message || 'Assign failed'); }
    };
    const handleRemove = async (employeeId) => {
        try {
            await axiosInstance.delete(`/shifts/${empModal.shift.id}/employees/${employeeId}`);
            toast.success('Employee removed');
            setEmpModal(p => ({
                ...p,
                shiftEmployees: p.shiftEmployees.filter(e => e.id !== employeeId),
            }));
            fetchShifts();
        } catch { toast.error('Remove failed'); }
    };

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold mb-1">
                        <Clock size={22} className="me-2 text-primary"/>Shift Management
                    </h4>
                    <p className="text-muted small mb-0">Define work shifts and assign employees</p>
                </div>
                <Button variant="primary" className="d-flex align-items-center gap-2" onClick={openCreate}>
                    <Plus size={18}/>New Shift
                </Button>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
            ) : (
                <Row className="g-3">
                    {shifts.length > 0 ? shifts.map(shift => {
                        const days = parseDays(shift.days_of_week);
                        return (
                            <Col key={shift.id} xs={12} sm={6} lg={4}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="p-4">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <h6 className="fw-bold mb-0">{shift.name}</h6>
                                            <div className="d-flex gap-1">
                                                <Button size="sm" variant="link" className="text-muted p-1"
                                                    onClick={() => openEdit(shift)}>
                                                    <Edit2 size={15}/>
                                                </Button>
                                                <Button size="sm" variant="link" className="text-danger p-1"
                                                    onClick={() => setConfirm({ show: true, id: shift.id, name: shift.name, loading: false })}>
                                                    <Trash2 size={15}/>
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <div style={{ fontSize: 22, fontWeight: 700, color: '#4f46e5' }}>
                                                {shift.start_time?.slice(0, 5)} – {shift.end_time?.slice(0, 5)}
                                            </div>
                                            <div className="text-muted small">
                                                {workHours(shift.start_time, shift.end_time, shift.break_minutes)} net
                                                · {shift.break_minutes || 0}m break
                                            </div>
                                        </div>
                                        <div className="d-flex gap-1 mb-3 flex-wrap">
                                            {DAYS.map((d, i) => (
                                                <span key={i} style={{
                                                    padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                                                    background: days.includes(i) ? '#eef2ff' : '#f1f5f9',
                                                    color:      days.includes(i) ? '#4f46e5' : '#94a3b8',
                                                }}>{d}</span>
                                            ))}
                                        </div>
                                        <Button variant="outline-primary" size="sm"
                                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                                            onClick={() => openEmpModal(shift)}>
                                            <Users size={14}/>{shift.employee_count || 0} Employees
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    }) : (
                        <Col xs={12}>
                            <Card className="border-0 shadow-sm p-5 text-center">
                                <Clock size={40} className="text-muted mx-auto mb-3 d-block opacity-50"/>
                                <h6 className="text-muted fw-bold">No shifts defined yet</h6>
                                <p className="text-muted small mb-3">Create work shifts and assign employees to them.</p>
                                <Button variant="primary" onClick={openCreate}>
                                    <Plus size={16} className="me-1"/>Create First Shift
                                </Button>
                            </Card>
                        </Col>
                    )}
                </Row>
            )}

            {/* ── Create / Edit Modal ─────────────────────────────────────── */}
            <Modal show={modal.show} onHide={closeModal} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold">
                        {modal.editing ? 'Edit Shift' : 'Create New Shift'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Shift Name *</Form.Label>
                            <Form.Control
                                placeholder="e.g. Morning Shift"
                                value={form.name}
                                isInvalid={attempted && !!errors.name}
                                onChange={e => setField('name', e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Start Time *</Form.Label>
                            <Form.Control type="time" value={form.start_time}
                                isInvalid={attempted && !!errors.start_time}
                                onChange={e => setField('start_time', e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{errors.start_time}</Form.Control.Feedback>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">End Time *</Form.Label>
                            <Form.Control type="time" value={form.end_time}
                                isInvalid={attempted && !!errors.end_time}
                                onChange={e => setField('end_time', e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{errors.end_time}</Form.Control.Feedback>
                            {form.start_time && form.end_time &&
                             toMins(form.end_time) < toMins(form.start_time) && (
                                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                                    🌙 Overnight shift detected
                                </div>
                            )}
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Break Duration (minutes)</Form.Label>
                            <Form.Control type="number" min="0" max="480"
                                value={form.break_minutes}
                                isInvalid={attempted && !!errors.break_minutes}
                                onChange={e => setField('break_minutes', parseInt(e.target.value) || 0)}/>
                            <Form.Control.Feedback type="invalid">{errors.break_minutes}</Form.Control.Feedback>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">
                                Working Days *
                                {attempted && errors.days_of_week && (
                                    <span className="text-danger ms-2" style={{ fontSize: 12, fontWeight: 400 }}>
                                        {errors.days_of_week}
                                    </span>
                                )}
                            </Form.Label>
                            <div className="d-flex gap-2 flex-wrap">
                                {DAYS.map((d, i) => (
                                    <button key={i} type="button"
                                        style={{
                                            padding: '6px 12px', borderRadius: 6, fontSize: 12,
                                            fontWeight: 600, cursor: 'pointer', border: '1.5px solid',
                                            borderColor: form.days_of_week.includes(i) ? '#4f46e5'
                                                : (attempted && errors.days_of_week ? '#dc3545' : '#e2e8f0'),
                                            background: form.days_of_week.includes(i) ? '#eef2ff' : '#fff',
                                            color:      form.days_of_week.includes(i) ? '#4f46e5' : '#94a3b8',
                                        }}
                                        onClick={() => toggleDay(i)}>
                                        {d}
                                    </button>
                                ))}
                            </div>
                        </Col>
                        {form.start_time && form.end_time && !errors.end_time && (
                            <Col xs={12}>
                                <div className="p-2 rounded bg-light small text-muted">
                                    Net work time:{' '}
                                    <strong>{workHours(form.start_time, form.end_time, form.break_minutes)}</strong>
                                    {' '}(after {form.break_minutes || 0}m break)
                                </div>
                            </Col>
                        )}
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={closeModal}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : modal.editing ? 'Save Changes' : 'Create Shift'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Employee Assignment Modal ───────────────────────────────── */}
            {empModal.shift && (
                <Modal show={empModal.show}
                    onHide={() => setEmpModal(p => ({ ...p, show: false }))}
                    centered size="lg">
                    <Modal.Header closeButton className="border-0">
                        <Modal.Title className="fw-bold">
                            <Users size={18} className="me-2"/>Employees — {empModal.shift?.name}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="d-flex gap-2 mb-4">
                            <Form.Select value={assignEmp}
                                onChange={e => setAssignEmp(e.target.value)}
                                className="flex-grow-1">
                                <option value="">Select employee to assign…</option>
                                {employees
                                    .filter(e => !empModal.shiftEmployees.some(se => se.id === e.id))
                                    .map(e => (
                                        <option key={e.id} value={e.id}>
                                            {e.name}{e.code ? ` (${e.code})` : ''}
                                        </option>
                                    ))}
                            </Form.Select>
                            <Button variant="primary" onClick={handleAssign} disabled={!assignEmp}>
                                <UserPlus size={16} className="me-1"/>Assign
                            </Button>
                        </div>
                        <h6 className="fw-bold mb-2">
                            Current Employees ({empModal.shiftEmployees.length})
                        </h6>
                        {empModal.shiftEmployees.length > 0 ? (
                            <ListGroup variant="flush">
                                {empModal.shiftEmployees.map(emp => (
                                    <ListGroup.Item key={emp.id}
                                        className="d-flex justify-content-between align-items-center px-0">
                                        <div>
                                            <span className="fw-medium">
                                                {emp.first_name} {emp.last_name}
                                            </span>
                                            {emp.employee_code && (
                                                <span className="text-muted small ms-2">({emp.employee_code})</span>
                                            )}
                                            {emp.effective_from && (
                                                <span className="text-muted small ms-2">
                                                    · since {new Date(emp.effective_from).toLocaleDateString('en-IN')}
                                                </span>
                                            )}
                                        </div>
                                        <Button size="sm" variant="outline-danger"
                                            onClick={() => handleRemove(emp.id)}>
                                            <X size={14}/>
                                        </Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        ) : (
                            <p className="text-muted small">No employees assigned to this shift yet.</p>
                        )}
                    </Modal.Body>
                </Modal>
            )}

            {/* ── Delete Confirm ──────────────────────────────────────────── */}
            <ConfirmModal
                show={confirm.show}
                title="Delete Shift"
                message={`Delete shift "${confirm.name}"? Employees assigned to it will be unassigned.`}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirm({ show: false, id: null, name: '', loading: false })}
                loading={confirm.loading}/>
        </div>
    );
};
export default ShiftManagement;
